import { useCallback, useEffect, useState } from 'react'
import { api, type VercelWebhookInfo } from '../api/client'

type Props = {
  getAuthToken: () => Promise<string>
  refreshTrigger: number
}

export function VercelWebhookSetup({ getAuthToken, refreshTrigger }: Props) {
  const [info, setInfo] = useState<VercelWebhookInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await getAuthToken()
      const data = await api.getVercelWebhook(token)
      setInfo(data)
    } catch (err) {
      setError((err as Error).message)
      setInfo(null)
    } finally {
      setLoading(false)
    }
  }, [getAuthToken])

  useEffect(() => {
    void load()
  }, [load, refreshTrigger])

  async function handleCopy() {
    if (!info?.webhook_url) return
    try {
      await navigator.clipboard.writeText(info.webhook_url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="sentry-webhook-panel">
      <h3 className="sentry-webhook-title">Investigate failed deployments</h3>
      <p className="sentry-webhook-intro">
        Connect your Vercel API credentials so SelfHeal can read deployment logs. Investigations start automatically when a deployment fails — via webhooks (Pro) or polling (Hobby / Free).
      </p>

      <div className="sentry-webhook-hint" role="note">
        <strong>Vercel Hobby / Free:</strong> project webhooks require a Pro plan. Save your API
        token and set <strong>Project to watch (Hobby / Free plan)</strong> in the credentials form
        above (project name or ID). SelfHeal polls Vercel for failed deployments on that project —
        no Vercel webhook needed.
      </div>

      <p className="sentry-webhook-muted">
        <strong>Vercel Pro and above:</strong> use the webhook URL below for instant notifications.
      </p>

      <ol className="sentry-webhook-steps">
        <li>
          Save <strong>API Token</strong> (and <strong>Team ID</strong> for team accounts).
        </li>
        <li>
          <strong>Hobby / Free:</strong> fill <strong>Project to watch</strong> and save — skip the
          webhook steps.
        </li>
        <li>
          <strong>Pro+:</strong> in <strong>Webhook secret (you create this)</strong>, paste any long
          random string you generate (SelfHeal does not assign one). Example: run{' '}
          <code>openssl rand -hex 32</code> in a terminal, copy the output, save credentials —
          then the URL below updates with that value as <code>?token=…</code>.
        </li>
        <li>
          <strong>Pro+:</strong> in Vercel: project <strong>Settings → Webhooks</strong> → add URL
          → enable <strong>Deployment Error</strong>.
        </li>
      </ol>

      {loading && <p className="sentry-webhook-muted">Loading webhook URL…</p>}
      {error && <p className="cred-error">Could not load webhook URL: {error}</p>}

      {info && !loading && (
        <>
          {!info.webhook_secret_configured && (
            <p className="sentry-webhook-hint">
              The URL above contains a placeholder. Create your own random secret, save it in the credentials form, then copy the updated URL here.
            </p>
          )}
          {!info.api_credentials_configured && (
            <p className="sentry-webhook-hint">
              Save your API token above — SelfHeal needs it to read Vercel deployment logs.
            </p>
          )}
          <div className="sentry-webhook-url-row">
            <input
              className="cred-input sentry-webhook-url-input"
              type="text"
              readOnly
              value={info.webhook_url}
              aria-label="Vercel webhook URL"
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              className="sentry-webhook-copy-btn"
              onClick={() => void handleCopy()}
              disabled={!info.webhook_url}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="sentry-webhook-muted">
            Organization ID: <code>{info.tenant_id}</code>
          </p>
        </>
      )}
    </div>
  )
}
